import { useEffect } from 'react'
import type { ReactNode } from 'react'

interface LightModeWrapperProps {
  children: ReactNode
}

/**
 * LightModeWrapper - Forces light mode for public marketing pages
 *
 * This component locks the page to light mode only, regardless of the user's
 * theme preference. It's used for public-facing marketing pages to ensure
 * consistent brand presentation.
 *
 * The component:
 * 1. Saves the current theme on mount
 * 2. Forces light mode by setting all theme-related attributes
 * 3. Restores the original theme when the component unmounts
 *
 * Usage:
 * ```tsx
 * <Route path="/" element={
 *   <LightModeWrapper>
 *     <HomePage />
 *   </LightModeWrapper>
 * } />
 * ```
 */
export const LightModeWrapper: React.FC<LightModeWrapperProps> = ({ children }) => {
  useEffect(() => {
    // Save the current theme attributes
    const originalTheme = document.documentElement.getAttribute('data-theme')
    const originalColorMode = document.documentElement.getAttribute('data-color-mode')
    const originalChakraColorMode = document.documentElement.getAttribute('data-chakra-color-mode')
    const originalColorScheme = document.documentElement.style.getPropertyValue('color-scheme')

    // Force light mode by setting all theme-related attributes
    document.documentElement.setAttribute('data-theme', 'light')
    document.documentElement.setAttribute('data-color-mode', 'light')
    document.documentElement.setAttribute('data-chakra-color-mode', 'light')
    document.documentElement.style.setProperty('color-scheme', 'light')

    // Cleanup: restore original theme when component unmounts
    return () => {
      if (originalTheme) {
        document.documentElement.setAttribute('data-theme', originalTheme)
      }
      if (originalColorMode) {
        document.documentElement.setAttribute('data-color-mode', originalColorMode)
      }
      if (originalChakraColorMode) {
        document.documentElement.setAttribute('data-chakra-color-mode', originalChakraColorMode)
      }
      if (originalColorScheme) {
        document.documentElement.style.setProperty('color-scheme', originalColorScheme)
      }
    }
  }, [])

  return <>{children}</>
}
